/**
 * cro.js — Phrena-style conversion layer for Motion.
 *
 * Self-contained island. Reuses Motion's existing cart pipeline:
 *   - Adds the in-cart upsell via /cart/add.js, then dispatches `ajaxProduct:added`
 *     so Motion's theme.CartDrawer rebuilds the drawer and opens it.
 *   - Toggles upsell visibility from cart state (cart:updated / ajaxProduct:added).
 *   - Shows a sticky / mobile add-to-cart bar once the main buy box scrolls out of view,
 *     keeping price in sync with Motion's `variant:change` event and delegating the
 *     actual add to the real product form (so all variant logic is reused).
 *
 * No external dependencies. Loaded with `defer`.
 */
(function () {
  'use strict';

  var routes = (window.theme && theme.routes) || {};
  var ADD_URL = routes.cartAdd || '/cart/add.js';
  var CART_URL = routes.cart || '/cart.js';

  function money(cents) {
    if (window.theme && theme.Currency && typeof theme.Currency.formatMoney === 'function') {
      return theme.Currency.formatMoney(cents, theme.settings && theme.settings.moneyFormat);
    }
    return '$' + (cents / 100).toFixed(2);
  }

  function getCart() {
    return fetch(CART_URL, { headers: { 'X-Requested-With': 'XMLHttpRequest' } }).then(function (r) {
      return r.json();
    });
  }

  /* ------------------------------------------------------------------ *
   *  In-cart upsell
   * ------------------------------------------------------------------ */
  function initUpsell() {
    var upsell = document.querySelector('[data-cart-upsell]');
    if (!upsell) return;

    var btn = upsell.querySelector('[data-cart-upsell-add]');
    var label = upsell.querySelector('[data-upsell-label]');
    var productId = parseInt(upsell.getAttribute('data-upsell-product-id'), 10);

    function syncVisibility(cart) {
      var inCart = cart && cart.items && cart.items.some(function (i) {
        return i.product_id === productId;
      });
      var empty = !cart || cart.item_count === 0;
      upsell.hidden = !!inCart || empty;
    }

    if (btn) {
      btn.addEventListener('click', function () {
        if (btn.classList.contains('is-loading')) return;
        var id = parseInt(btn.getAttribute('data-variant-id'), 10);
        if (!id) return;

        btn.classList.add('is-loading');
        if (label) label.textContent = 'Adding…';

        fetch(ADD_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ items: [{ id: id, quantity: 1 }] })
        })
          .then(function (r) { return r.json(); })
          .then(function () {
            // Let Motion rebuild + open the drawer.
            document.dispatchEvent(new CustomEvent('ajaxProduct:added', { bubbles: true }));
          })
          .catch(function () {})
          .then(function () {
            btn.classList.remove('is-loading');
            if (label) label.textContent = 'Add to order';
          });
      });
    }

    // Refresh visibility on every cart mutation.
    document.addEventListener('cart:updated', function (e) {
      if (e.detail && e.detail.cart) {
        syncVisibility(e.detail.cart);
      } else {
        getCart().then(syncVisibility);
      }
    });
    document.addEventListener('ajaxProduct:added', function () {
      getCart().then(syncVisibility);
    });

    // Initial state.
    getCart().then(syncVisibility);
  }

  /* ------------------------------------------------------------------ *
   *  Sticky / mobile add-to-cart bar
   * ------------------------------------------------------------------ */
  function initStickyBar() {
    var bar = document.querySelector('[data-sticky-atc]');
    if (!bar) return;

    var formId = bar.getAttribute('data-form-id');
    var heroAtc = document.querySelector('[data-pph-atc]');
    var form = formId ? document.getElementById(formId) : document.querySelector('.product-single__form');

    // Prefer the Phrena hero CTA when present; otherwise use Motion's product form.
    // (Avoids accidentally targeting a product-recommendations / quick-add form.)
    var realBtn = heroAtc || (form ? form.querySelector('[data-add-to-cart]') : null);
    if (!realBtn) return;

    var trigger = bar.querySelector('[data-sticky-atc-trigger]');
    var priceEl = bar.querySelector('[data-sticky-price]');
    var compareEl = bar.querySelector('[data-sticky-compare]');

    // Trigger the real add-to-cart so all of Motion's variant + drawer logic runs.
    if (trigger && realBtn) {
      trigger.addEventListener('click', function (e) {
        e.preventDefault();
        if (realBtn.disabled) return;
        realBtn.click();
      });
    }

    // Mirror the Phrena hero's bundle selection (formatted money strings).
    document.addEventListener('pph:variantchange', function (e) {
      var d = e.detail || {};
      if (priceEl && d.price) priceEl.textContent = d.price;
      if (compareEl) {
        if (d.compare) { compareEl.textContent = d.compare; compareEl.hidden = false; }
        else { compareEl.hidden = true; }
      }
      if (trigger) trigger.classList.toggle('is-unavailable', d.available === false);
    });

    // Keep price + availability in sync with Motion variant changes (if a form is present).
    document.addEventListener('variant:change', function (e) {
      var variant = e.detail && e.detail.variant;
      if (!variant) return;
      if (priceEl) priceEl.innerHTML = money(variant.price);
      if (compareEl) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          compareEl.innerHTML = money(variant.compare_at_price);
          compareEl.hidden = false;
        } else {
          compareEl.hidden = true;
        }
      }
      if (trigger) {
        trigger.disabled = !variant.available;
        trigger.classList.toggle('is-unavailable', !variant.available);
      }
    });

    // ---- Reflect the selected Kaching bundle price/qty in the sticky bar ----
    // (The bar renders the raw single-unit variant price; Kaching's real price
    //  lives in the selected bundle.)
    kachingSyncSticky();
    document.addEventListener('change', function (e) {
      var t = e.target;
      if (t && t.name && t.name.indexOf('kaching-bundles-deal') === 0) kachingSyncSticky();
    });
    document.addEventListener('click', function (e) {
      if (e.target && e.target.closest &&
          e.target.closest('kaching-bundle, kaching-bundles-block, [data-kaching-deal-id]')) {
        setTimeout(kachingSyncSticky, 80);
      }
    });
    // Kaching's embed loads async — poll briefly so the default bundle price shows.
    var kSyncTries = 0;
    var kSyncIv = setInterval(function () {
      kSyncTries++;
      kachingSyncSticky();
      var ready = kachingDealsById && document.querySelector('input[name^="kaching-bundles-deal"]:checked');
      if (ready || kSyncTries > 20) clearInterval(kSyncIv);
    }, 300);

    // Reveal the bar once the main buy box has scrolled out of view.
    var anchor = document.querySelector('[data-sticky-atc-anchor]') || realBtn;
    if (anchor && 'IntersectionObserver' in window) {
      var observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          bar.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0);
        });
      }, { rootMargin: '0px 0px -80% 0px', threshold: 0 });
      observer.observe(anchor);
    } else {
      bar.classList.add('is-visible');
    }

    // Avoid overlapping the cart drawer.
    document.addEventListener('cart:open', function () { bar.classList.add('is-suppressed'); });
    document.addEventListener('cart:close', function () { bar.classList.remove('is-suppressed'); });
  }

  /* ------------------------------------------------------------------ *
   *  Kaching Bundles bridge
   *
   *  This buy box has no <form action="/cart/add">, and Kaching's
   *  customSelectors (addToCartButton / quantity) are unset, so Kaching never
   *  hooks our add-to-cart. Without this bridge the hero always adds 1 unit,
   *  whichever bundle is selected.
   *
   *  The app exposes window.kachingBundlesApi.fetchDeals(), which resolves to
   *  its deal bars — each with an id matching the radio's value, plus the
   *  quantity that bundle represents, e.g.
   *    { id: 'jPmy', title: 'Trail Duo', dealBarType: 'quantity-break', quantity: 2 }
   *
   *  So: read the checked radio, map it to that quantity, and add that many
   *  units. The bundle price is a Shopify automatic discount, so it applies on
   *  its own once the correct quantity is in the cart.
   * ------------------------------------------------------------------ */
  var kachingDealsById = null;   // { dealBarId: { qty, price(cents) } }
  var kachingDealsList = [];     // same deals in render order (positional fallback)
  var kachingLoading = null;

  // Every Kaching deal radio, in DOM order.
  function kachingDealRadios() {
    return [].slice.call(document.querySelectorAll('input[name^="kaching-bundles-deal"]'));
  }

  function kachingApiReady() {
    return window.kachingBundlesApi && typeof window.kachingBundlesApi.fetchDeals === 'function';
  }

  function kachingLoadDeals() {
    if (kachingDealsById) return Promise.resolve(kachingDealsById);
    if (kachingLoading) return kachingLoading;
    if (!kachingApiReady()) return Promise.resolve(null);

    var res;
    try { res = window.kachingBundlesApi.fetchDeals(); } catch (e) { return Promise.resolve(null); }
    if (!res || typeof res.then !== 'function') return Promise.resolve(null);

    kachingLoading = res.then(function (deals) {
      var map = {};
      var list = [];
      (deals || []).forEach(function (deal) {
        ((deal && deal.dealBars) || []).forEach(function (bar) {
          if (!bar || !bar.id) return;
          // Bundle total price in cents (only for "specific"-type deals, e.g. $119.95).
          var priceCents = null;
          if (bar.discount && bar.discount.type === 'specific' && bar.discount.value != null) {
            priceCents = Math.round(bar.discount.value * 100);
          }
          var entry = {
            qty: parseInt(bar.quantity, 10) || 1,
            price: priceCents,
            freeGifts: bar.freeGifts || [],
            upsells: bar.upsells || []
          };
          map[bar.id] = entry;
          list.push(entry);
        });
      });
      kachingDealsById = map;
      kachingDealsList = list;
      return map;
    }).catch(function () {
      kachingLoading = null;   // allow a retry on the next click
      return null;
    });

    return kachingLoading;
  }

  // The currently selected Kaching bundle: { qty, price(cents) }, or null.
  //
  // Matched by bar id first. If that misses — which happens when Kaching serves
  // a cached widget from one deal while its API already returns another (ids
  // differ), e.g. right after switching the active deal — fall back to the
  // SELECTED BAR'S POSITION. Without that fallback the deal resolves to null and
  // the add silently degrades to a single unit at full price.
  function kachingSelectedDeal() {
    var radios = kachingDealRadios();
    var idx = -1;
    for (var i = 0; i < radios.length; i++) {
      if (radios[i].checked) { idx = i; break; }
    }
    if (idx < 0) return Promise.resolve(null);
    var id = radios[idx].value;

    function pick(map) {
      if (map && map[id]) return map[id];
      if (kachingDealsList && kachingDealsList[idx]) return kachingDealsList[idx];
      return null;
    }

    if (kachingDealsById) {
      var found = pick(kachingDealsById);
      if (found) return Promise.resolve(found);
    }
    return kachingLoadDeals().then(pick);
  }

  // How many units the SELECTED bar shows, counted straight from Kaching's own
  // per-unit colour selectors. This is what the shopper can see, so it beats any
  // cached/mismatched API data as a quantity signal.
  function kachingSelectedUnitCount() {
    var checked = document.querySelector('input[name^="kaching-bundles-deal"]:checked');
    if (!checked) return 0;
    var bar = checked.closest('.kaching-bundles__bar');
    if (!bar) return 0;
    return bar.querySelectorAll('.kaching-bundles__bar-variant__content').length;
  }

  // Map of option/color name (lowercased) -> variant id, from the product JSON.
  var kachingVariantMapCache = null;
  var kachingVariantMapLoading = null;
  function kachingLoadVariantMap() {
    if (kachingVariantMapCache) return Promise.resolve(kachingVariantMapCache);
    if (kachingVariantMapLoading) return kachingVariantMapLoading;
    var path = window.location.pathname || '';
    var handle = path.indexOf('/products/') > -1 ? path.split('/products/')[1].split('/')[0].split('?')[0] : '';
    if (!handle) return Promise.resolve(null);
    kachingVariantMapLoading = fetch('/products/' + handle + '.js', { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (p) {
        if (!p || !p.variants) return null;
        var map = {};
        p.variants.forEach(function (v) {
          if (v.title) map[String(v.title).replace(/\s+/g, ' ').trim().toLowerCase()] = v.id;
          (v.options || []).forEach(function (o) {
            if (o) map[String(o).replace(/\s+/g, ' ').trim().toLowerCase()] = v.id;
          });
        });
        kachingVariantMapCache = map;
        return map;
      })
      .catch(function () { kachingVariantMapLoading = null; return null; });
    return kachingVariantMapLoading;
  }

  // The per-unit variant IDs selected in the CURRENT Kaching bundle, read from
  // Kaching's own color dropdowns (it exposes no API/form fields for these).
  // Resolves to [variantId, ...] or null if it can't be read reliably.
  function kachingSelectedVariants() {
    var checked = document.querySelector('input[name^="kaching-bundles-deal"]:checked');
    if (!checked) return Promise.resolve(null);
    var bar = checked.closest('.kaching-bundles__bar');   // scope to the SELECTED deal only
    if (!bar) return Promise.resolve(null);
    var els = bar.querySelectorAll('.kaching-bundles__bar-variant__content');
    if (!els.length) return Promise.resolve(null);
    var names = Array.prototype.map.call(els, function (e) {
      return String(e.textContent || '').replace(/\s+/g, ' ').trim().toLowerCase();
    });
    return kachingLoadVariantMap().then(function (map) {
      if (!map) return null;
      var ids = names.map(function (n) { return map[n] || null; });
      if (ids.some(function (id) { return !id; })) return null;   // unmapped color -> bail to fallback
      return ids;
    });
  }

  // Build the /cart/add items for the current Kaching selection: the correct
  // quantity AND the per-unit colors the shopper picked (so a 2-pack of Black +
  // Camo adds 1 of each). Falls back to the default variant x quantity if the
  // colors can't be read or don't line up with the bundle quantity.
  function kachingBuildItems(fallbackVariantId, nativeQty) {
    return Promise.all([kachingSelectedDeal(), kachingSelectedVariants()]).then(function (res) {
      var deal = res[0], variantIds = res[1];
      var unitCount = kachingSelectedUnitCount();
      var items;

      // Quantity, most trustworthy source first:
      //  1. the per-unit colours the shopper actually picked,
      //  2. the number of unit selectors the selected bar renders,
      //  3. the matched deal's quantity,
      //  4. the native quantity input.
      // The DOM outranks the API deliberately: if Kaching's cached widget and its
      // API disagree, the widget is what the shopper saw and agreed to buy.
      if (variantIds && variantIds.length) {
        var counts = {};
        variantIds.forEach(function (id) { counts[id] = (counts[id] || 0) + 1; });
        items = Object.keys(counts).map(function (id) {
          return { id: parseInt(id, 10), quantity: counts[id] };
        });
      } else {
        var qty = unitCount || (deal && deal.qty) || nativeQty || 1;
        items = [{ id: parseInt(fallbackVariantId, 10), quantity: qty }];
      }

      // Never charge for more units than the shopper was shown, and never fewer.
      var expected = unitCount || (deal && deal.qty) || 0;
      if (expected > 0) {
        var total = items.reduce(function (n, it) { return n + it.quantity; }, 0);
        if (total !== expected) {
          if (window.console) {
            window.console.warn('[cro] bundle quantity mismatch — using the bar the shopper saw', { built: total, expected: expected });
          }
          items = [{ id: parseInt(fallbackVariantId, 10), quantity: expected }];
        }
      }
      // Append the selected bundle's free gift(s) + upsell(s), read from Kaching
      // and resolved to real variants. The price is whatever the linked product
      // is in Shopify (gift product = $0 -> free; upsell = its paid price).
      return kachingExtraItems(deal).then(function (extra) {
        return items.concat(extra);
      });
    });
  }

  /* ------------------------------------------------------------------ *
   *  Free gift / upsell (Kaching-linked, resolved to variants)
   *
   *  Kaching cannot add these in this custom buy box (it won't hook the
   *  add-to-cart) and won't price a bridge-added gift free (both verified). So
   *  the bridge adds them, and the price is REAL: the gift is a $0.00 product /
   *  variant in Shopify, the upsell a paid one.
   *
   *  Kaching's deal data gives the linked product (and sometimes a variant). We
   *  use the variant if present, else resolve the product to its variant via the
   *  Storefront API (Kaching exposes the shop domain + a storefront token in its
   *  config). A single-variant gift/upsell product resolves unambiguously.
   *
   *  SETUP: the gift bundle must be the ACTIVE Kaching deal (the one rendered on
   *  the page), and its gift product must be priced $0.00.
   * ------------------------------------------------------------------ */
  function kachingConfig() {
    var el = document.getElementById('kaching-bundles-config');
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  var kachingVariantByProduct = {};   // productId -> variantId (or null)
  function kachingResolveVariant(productId) {
    var pid = String(productId).replace(/\D/g, '');
    if (!pid) return Promise.resolve(null);
    if (kachingVariantByProduct[pid] !== undefined) return Promise.resolve(kachingVariantByProduct[pid]);
    var cfg = kachingConfig();
    if (!cfg || !cfg.shopifyDomain || !cfg.storefrontAccessToken) return Promise.resolve(null);
    var q = 'query($id: ID!){ node(id:$id){ ... on Product { variants(first:50){ edges { node { id availableForSale } } } } } }';
    return fetch('https://' + cfg.shopifyDomain + '/api/2024-07/graphql.json', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': cfg.storefrontAccessToken },
      body: JSON.stringify({ query: q, variables: { id: 'gid://shopify/Product/' + pid } })
    })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (j) {
        var edges = (j && j.data && j.data.node && j.data.node.variants && j.data.node.variants.edges) || [];
        var pick = null;
        for (var i = 0; i < edges.length; i++) {
          var vid = parseInt(String(edges[i].node.id).replace(/\D/g, ''), 10);
          if (edges[i].node.availableForSale) { pick = vid; break; }
          if (pick === null) pick = vid;   // fallback: first variant even if unavailable
        }
        kachingVariantByProduct[pid] = pick;
        return pick;
      })
      .catch(function () { kachingVariantByProduct[pid] = null; return null; });
  }

  // Is the upsell ticked? Kaching's toggle is NOT a real <input> — it renders
  //   <span role="button" class="kaching-bundles__upsell__checkbox
  //                              kaching-bundles__upsell__checkbox--selected">
  // and carries the state purely in that "--selected" class (verified live,
  // 28 Jul 2026: clicking it adds/removes the modifier). Both add-to-cart
  // buttons route through this. Scoped to the SELECTED bar.
  function kachingUpsellChecked() {
    var checked = document.querySelector('input[name^="kaching-bundles-deal"]:checked');
    var bar = checked && checked.closest('.kaching-bundles__bar');
    var scope = bar || document;

    var el = scope.querySelector('.kaching-bundles__upsell__checkbox');
    if (el) {
      var aria = el.getAttribute('aria-checked');
      if (aria != null) return aria !== 'false';
      return /--selected/.test(String(el.className || ''));
    }

    // Fallback: some Kaching versions/settings render a real checkbox.
    var cb = scope.querySelector('[class*="upsell"] input[type="checkbox"]');
    if (cb) return cb.checked;

    // No toggle at all: add only if an upsell row is rendered (config always-on);
    // if the bar shows no upsell, there is nothing the shopper opted into.
    return !!scope.querySelector('[class*="upsell"]');
  }

  function kachingExtraItems(deal) {
    if (!deal) return Promise.resolve([]);
    // Tag free gifts so the gift guard can pull them if the cart stops
    // qualifying; carry the bundle quantity they require (_gift_min).
    var minQty = deal.qty || 1;
    var upsellOn = kachingUpsellChecked();
    var entries = (deal.freeGifts || []).map(function (g) { return { g: g, gift: true }; })
                    .concat(upsellOn ? (deal.upsells || []).map(function (g) { return { g: g, gift: false }; }) : []);
    if (!entries.length) return Promise.resolve([]);
    return Promise.all(entries.map(function (entry) {
      var g = entry.g;
      if (!g || typeof g !== 'object') return null;
      var qty = parseInt(g.quantity, 10) || 1;
      var props = entry.gift ? { _free_gift: 'true', _gift_min: String(minQty) } : null;
      function make(id) { var it = { id: id, quantity: qty }; if (props) it.properties = props; return it; }
      // Prefer an explicit variant if Kaching provides one.
      var directRaw = g.variantId || g.variant_id ||
                      (g.variantIds && g.variantIds.length && g.variantIds[0]) ||
                      (g.variant && (g.variant.id || g.variant.gid));
      if (directRaw) {
        var did = parseInt(String(directRaw).replace(/\D/g, ''), 10);
        return did ? make(did) : null;
      }
      var pid = g.productId || g.product_id || g.productGID;
      if (!pid) return null;
      return kachingResolveVariant(pid).then(function (vid) {
        return vid ? make(vid) : null;
      });
    })).then(function (arr) {
      return arr.filter(function (x) { return !!x; });
    });
  }

  /* ------------------------------------------------------------------ *
   *  Free-gift guard
   *  Removes a free gift once the cart no longer qualifies for it (fewer vest
   *  units than the bundle the gift is tied to), so a shopper can't take the
   *  Duo, delete a vest, and keep the freebie. Runs on every cart change.
   * ------------------------------------------------------------------ */
  function initGiftGuard() {
    var CHANGE_URL = routes.cartChange || '/cart/change.js';
    var VEST_HANDLE = 'vest';
    var busy = false;

    function vestUnits(cart) {
      return (cart.items || []).reduce(function (n, i) {
        return i.handle === VEST_HANDLE ? n + (i.quantity || 0) : n;
      }, 0);
    }

    function reconcile(cart) {
      if (busy || !cart) return;
      var vests = vestUnits(cart);
      var offending = (cart.items || []).filter(function (i) {
        var p = i.properties || {};
        if (p._free_gift !== 'true') return false;
        var min = parseInt(p._gift_min, 10) || 2;
        return vests < min;
      });
      if (!offending.length) return;
      busy = true;
      fetch(CHANGE_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ id: offending[0].key, quantity: 0 })
      }).then(function () {
        document.dispatchEvent(new CustomEvent('cart:build', { bubbles: true }));
        document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
      }).catch(function () {}).then(function () {
        busy = false;
        // Re-check in case there were multiple gift lines to pull.
        getCart().then(reconcile);
      });
    }

    document.addEventListener('cart:updated', function (e) {
      if (e.detail && e.detail.cart) reconcile(e.detail.cart);
      else getCart().then(reconcile);
    });
    document.addEventListener('ajaxProduct:added', function () { getCart().then(reconcile); });
    getCart().then(reconcile);
  }

  // Quantity for the currently selected Kaching bundle (or null).
  function kachingSelectedQty() {
    return kachingSelectedDeal().then(function (deal) { return deal ? deal.qty : null; });
  }

  // Mirror the selected Kaching bundle's price + compare-at into the sticky bar,
  // which otherwise shows the raw single-unit variant price ($140 x 1).
  function kachingSyncSticky() {
    var bar = document.querySelector('[data-sticky-atc]');
    if (!bar) return;
    var priceEl = bar.querySelector('[data-sticky-price]');
    if (!priceEl) return;
    var compareEl = bar.querySelector('[data-sticky-compare]');
    var unit = parseInt(bar.getAttribute('data-unit-price'), 10) || 0;   // cents, e.g. 14000
    kachingSelectedDeal().then(function (deal) {
      if (!deal) return;
      // money() returns the shop's money HTML (e.g. <span class="money">$119.95</span>),
      // so use innerHTML — textContent would print the tags as literal text.
      if (deal.price != null) {
        priceEl.innerHTML = money(deal.price);              // e.g. $119.95
        if (compareEl && unit) {
          compareEl.innerHTML = money(unit * deal.qty);     // e.g. $280.00
          compareEl.hidden = false;
        }
      } else if (unit) {
        // No specific bundle price available — fall back to unit x qty.
        priceEl.innerHTML = money(unit * deal.qty);
        if (compareEl) compareEl.hidden = true;
      }
    });
  }

  // The app embed loads async — poll briefly so the map is warm before the
  // first click. A click before it's ready still resolves via kachingLoadDeals().
  function kachingWarm(tries) {
    if (kachingApiReady()) { kachingLoadDeals(); return; }
    if ((tries || 0) < 20) {
      setTimeout(function () { kachingWarm((tries || 0) + 1); }, 300);
    }
  }

  /* ------------------------------------------------------------------ *
   *  Phrena product hero (gallery + bundle selector + subscribe + ATC)
   * ------------------------------------------------------------------ */
  function initHero() {
    var hero = document.querySelector('[data-pph]');
    if (!hero) return;

    kachingWarm(0);

    /* ---- Gallery ---- */
    var slides = [].slice.call(hero.querySelectorAll('[data-pph-slide]'));
    var thumbs = [].slice.call(hero.querySelectorAll('[data-pph-thumb]'));
    var current = 0;

    function showSlide(i) {
      if (!slides.length) return;
      current = Math.max(0, Math.min(i, slides.length - 1));
      slides.forEach(function (s, idx) { s.classList.toggle('is-active', idx === current); });
      thumbs.forEach(function (t, idx) { t.classList.toggle('is-active', idx === current); });
      var active = thumbs[current];
      if (active && active.scrollIntoView) {
        active.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
      }
    }

    thumbs.forEach(function (t) {
      t.addEventListener('click', function () { showSlide(parseInt(t.getAttribute('data-pph-thumb'), 10)); });
    });
    var prev = hero.querySelector('[data-pph-prev]');
    var next = hero.querySelector('[data-pph-next]');
    if (prev) prev.addEventListener('click', function () { showSlide(current - 1); });
    if (next) next.addEventListener('click', function () { showSlide(current + 1); });

    /* ---- Bundle selector ---- */
    function syncBundle(radio) {
      hero.querySelectorAll('[data-pph-bundle]').forEach(function (card) {
        card.classList.toggle('is-selected', card.contains(radio) && radio.checked);
      });
      // Broadcast so the sticky bar can mirror the selected price.
      document.dispatchEvent(new CustomEvent('pph:variantchange', {
        detail: {
          price: radio.getAttribute('data-price') || '',
          compare: radio.getAttribute('data-compare') || '',
          available: radio.getAttribute('data-available') !== 'false'
        }
      }));
    }
    hero.querySelectorAll('[data-pph-variant]').forEach(function (radio) {
      radio.addEventListener('change', function () { syncBundle(radio); });
    });
    var checkedRadio = hero.querySelector('[data-pph-variant]:checked');
    if (checkedRadio) syncBundle(checkedRadio);

    /* ---- Subscribe / one-time toggle ---- */
    var sellingPlan = '';
    var planButtons = [].slice.call(hero.querySelectorAll('[data-pph-plan]'));
    planButtons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        planButtons.forEach(function (b) { b.classList.remove('is-active'); });
        btn.classList.add('is-active');
        sellingPlan = btn.getAttribute('data-pph-plan') || '';
      });
    });
    var activePlan = hero.querySelector('[data-pph-plan].is-active');
    if (activePlan) sellingPlan = activePlan.getAttribute('data-pph-plan') || '';

    /* ---- Add to cart (AJAX -> Motion drawer) ----
     * Proven live behavior: intercept the button click, add via AJAX and open
     * Motion's drawer. Items come from the Kaching selection (quantity +
     * per-unit colors + upsell) via the bridge.
     */
    var atc = hero.querySelector('[data-pph-atc]');
    var addUrl = hero.getAttribute('data-cart-add-url') || ADD_URL;
    if (atc) {
      atc.addEventListener('click', function () {
        var checked = hero.querySelector('[data-pph-variant]:checked');
        var variantId = (checked && checked.value) ? checked.value : hero.getAttribute('data-default-variant-id');
        if (!variantId) return;
        if (atc.classList.contains('is-loading')) return;

        var nativeQty = (checked && checked.getAttribute('data-quantity')) ? parseInt(checked.getAttribute('data-quantity'), 10) : 1;

        atc.classList.add('is-loading');
        var orig = atc.getAttribute('data-default-text') || atc.textContent;
        atc.textContent = 'Adding…';

        kachingBuildItems(variantId, nativeQty)
          .then(function (items) {
            if (sellingPlan) items.forEach(function (it) { it.selling_plan = parseInt(sellingPlan, 10); });
            // Diagnostic: exactly what this click adds and why. If the cart ever
            // disagrees with the widget, a screenshot of this line tells the story.
            if (window.console) {
              console.info('[cro-v19] ATC add', JSON.stringify({
                items: items,
                upsellChecked: kachingUpsellChecked()
              }));
            }
            return fetch(addUrl, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
              body: JSON.stringify({ items: items })
            });
          })
          .then(function (r) { return r.json(); })
          .then(function () {
            document.dispatchEvent(new CustomEvent('ajaxProduct:added', { bubbles: true }));
          })
          .catch(function () {})
          .then(function () {
            atc.classList.remove('is-loading');
            atc.textContent = orig;
          });
      });
    }
  }

  /* ------------------------------------------------------------------ *
   *  UGC video drawer
   * ------------------------------------------------------------------ */
  function initUgc() {
    document.querySelectorAll('[data-ugc]').forEach(function (root) {
      var drawer = root.querySelector('[data-ugc-drawer]');
      if (!drawer) return;
      var slides = [].slice.call(root.querySelectorAll('[data-ugc-slide]'));
      if (!slides.length) return;
      var current = -1;

      function stop(slide) {
        var v = slide.querySelector('[data-ugc-video]');
        if (v) { try { v.pause(); v.currentTime = 0; } catch (e) {} }
        var embed = slide.querySelector('[data-ugc-embed]');
        if (embed) embed.innerHTML = ''; // removing the iframe stops playback
      }

      function play(slide) {
        var v = slide.querySelector('[data-ugc-video]');
        if (v) { try { v.play(); } catch (e) {} return; }
        var embed = slide.querySelector('[data-ugc-embed]');
        if (embed && embed.getAttribute('data-ugc-embed') && !embed.querySelector('iframe')) {
          var url = embed.getAttribute('data-ugc-embed');
          var sep = url.indexOf('?') === -1 ? '?' : '&';
          var iframe = document.createElement('iframe');
          iframe.src = url + sep + 'autoplay=1';
          iframe.setAttribute('allow', 'autoplay; encrypted-media; picture-in-picture; fullscreen');
          iframe.setAttribute('allowfullscreen', '');
          iframe.setAttribute('frameborder', '0');
          embed.appendChild(iframe);
        }
      }

      function show(i) {
        if (i < 0) i = slides.length - 1;
        if (i >= slides.length) i = 0;
        slides.forEach(function (s, idx) {
          var active = idx === i;
          s.classList.toggle('is-active', active);
          if (!active) stop(s);
        });
        current = i;
        play(slides[i]);
      }

      function open(i) {
        drawer.hidden = false;
        drawer.setAttribute('aria-hidden', 'false');
        document.documentElement.classList.add('ugc-locked');
        show(i);
      }

      function close() {
        slides.forEach(stop);
        drawer.hidden = true;
        drawer.setAttribute('aria-hidden', 'true');
        document.documentElement.classList.remove('ugc-locked');
        current = -1;
      }

      root.querySelectorAll('[data-ugc-open]').forEach(function (card) {
        card.addEventListener('click', function () { open(parseInt(card.getAttribute('data-ugc-open'), 10) || 0); });
      });
      root.querySelectorAll('[data-ugc-close]').forEach(function (el) { el.addEventListener('click', close); });
      var prev = root.querySelector('[data-ugc-prev]');
      var next = root.querySelector('[data-ugc-next]');
      if (prev) prev.addEventListener('click', function () { show(current - 1); });
      if (next) next.addEventListener('click', function () { show(current + 1); });
      document.addEventListener('keydown', function (e) {
        if (drawer.hidden) return;
        if (e.key === 'Escape') close();
        else if (e.key === 'ArrowLeft' && prev) show(current - 1);
        else if (e.key === 'ArrowRight' && next) show(current + 1);
      });
    });
  }

  /* ------------------------------------------------------------------ *
   *  Q&A carousel — category filter tabs
   * ------------------------------------------------------------------ */
  function initQa() {
    document.querySelectorAll('[data-qa]').forEach(function (root) {
      var tabs = [].slice.call(root.querySelectorAll('[data-qa-tab]'));
      var cards = [].slice.call(root.querySelectorAll('[data-qa-cat]'));
      var row = root.querySelector('[data-qa-row]');
      if (!tabs.length) return;
      tabs.forEach(function (tab) {
        tab.addEventListener('click', function () {
          var cat = tab.getAttribute('data-qa-tab');
          tabs.forEach(function (t) { t.classList.toggle('is-active', t === tab); });
          cards.forEach(function (card) {
            card.hidden = !(cat === 'all' || card.getAttribute('data-qa-cat') === cat);
          });
          if (row) row.scrollLeft = 0;
        });
      });
    });
  }

  /* ------------------------------------------------------------------ *
   *  Social-proof stats carousel — arrow scrolling
   * ------------------------------------------------------------------ */
  function initSocialProof() {
    document.querySelectorAll('[data-sp-carousel]').forEach(function (root) {
      var row = root.querySelector('[data-sp-row]');
      var prev = root.querySelector('[data-sp-prev]');
      var next = root.querySelector('[data-sp-next]');
      if (!row) return;
      function step(dir) {
        var card = row.querySelector('.social-proof__card');
        var amount = card ? card.getBoundingClientRect().width + 16 : row.clientWidth * 0.7;
        row.scrollBy({ left: dir * amount, behavior: 'smooth' });
      }
      function update() {
        var max = row.scrollWidth - row.clientWidth - 2;
        var noOverflow = row.scrollWidth <= row.clientWidth + 2;
        if (prev) prev.classList.toggle('is-hidden', noOverflow || row.scrollLeft <= 2);
        if (next) next.classList.toggle('is-hidden', noOverflow || row.scrollLeft >= max);
      }
      if (prev) prev.addEventListener('click', function () { step(-1); });
      if (next) next.addEventListener('click', function () { step(1); });
      row.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);
      update();
    });
  }

  /* ------------------------------------------------------------------ *
   *  Review rating → smooth scroll to reviews (future-proof for AliReviews)
   * ------------------------------------------------------------------ */
  function reviewsTarget() {
    return document.querySelector(
      '[id*="ali-reviews" i], [id*="alireviews" i], [class*="alireviews" i], [class*="ali-reviews" i],' +
      '.jdgm-rev-widg, #looxReviews, [data-reviews-widget]'
    ) || document.getElementById('reviews') || document.querySelector('[data-reviews-anchor]');
  }
  function initReviewLink() {
    document.querySelectorAll('[data-scroll-reviews]').forEach(function (link) {
      link.addEventListener('click', function (e) {
        e.preventDefault();
        function go() {
          var target = reviewsTarget();
          var y;
          if (target) {
            y = target.getBoundingClientRect().top + window.pageYOffset - 72;
          } else {
            // No dedicated reviews widget found — reviews sit at the bottom, so go there.
            y = document.documentElement.scrollHeight - window.innerHeight;
          }
          window.scrollTo({ top: y, behavior: 'smooth' });
        }
        // Scroll now, then re-correct as lazy images/app widgets below finish loading
        // and shift the layout (fixes landing short at ~75% of the page).
        go();
        setTimeout(go, 450);
        setTimeout(go, 1000);
      });
    });
  }

  /**
   * Auto-add the free gift (eBook) to the cart.
   *
   * Pairs with the Shopify "Buy X Get Y" automatic discount: that discount makes
   * the eBook free once it is in the cart — this is what actually PUTS it there
   * (a Buy X Get Y discount never adds product Y on its own).
   *
   * SETUP — set GIFT_HANDLE below to the eBook product's URL handle:
   *   Shopify admin → Products → open the eBook → copy the last part of its URL,
   *   e.g. .../products/THIS-PART  (all lowercase, words joined by hyphens).
   * TRIGGER_HANDLES: the cart must contain one of these products for the gift to
   *   be added. Leave the array empty ([]) to add the gift whenever the cart has
   *   any other (non-gift) item — the safest default for a single-product store.
   */
  function initFreeGift() {
    var GIFT_HANDLE = 'the-ultimate-sciatica-guide-ebook';
    var TRIGGER_HANDLES = [];   // e.g. ['resteva']; [] = any non-gift item qualifies
    var GIFT_QTY = 1;

    if (!GIFT_HANDLE) return;

    var CHANGE_URL = routes.cartChange || '/cart/change.js';
    var giftVariantId = null;
    var resolveFailed = false;
    var busy = false;

    function resolveVariant() {
      if (giftVariantId) return Promise.resolve(giftVariantId);
      if (resolveFailed) return Promise.resolve(null);
      return fetch('/products/' + GIFT_HANDLE + '.js', { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (p) {
          if (!p || !p.variants || !p.variants.length) {
            resolveFailed = true;
            if (window.console) console.warn('[cro] free gift: product "' + GIFT_HANDLE + '" not found — check GIFT_HANDLE in cro.js');
            return null;
          }
          var available = p.variants.filter(function (v) { return v.available; });
          giftVariantId = (available[0] || p.variants[0]).id;
          return giftVariantId;
        })
        .catch(function () { resolveFailed = true; return null; });
    }

    function giftLine(cart) {
      var items = cart.items || [];
      for (var i = 0; i < items.length; i++) {
        if (items[i].handle === GIFT_HANDLE) return items[i];
      }
      return null;
    }

    function firstNonGift(cart) {
      var items = cart.items || [];
      for (var i = 0; i < items.length; i++) {
        if (items[i].handle !== GIFT_HANDLE) return items[i];
      }
      return null;
    }

    // Shopify inserts newly-added items at the TOP of the cart, so the auto-added
    // gift lands above the belt in the raw cart data — and therefore at checkout.
    // Re-add the qualifying product so it becomes the newest line and sits first
    // everywhere (drawer + checkout), with the gift below it.
    function reorderGiftLast() {
      return getCart().then(function (cart) {
        var items = cart.items || [];
        if (items.length < 2) return;
        if (items[0].handle !== GIFT_HANDLE) return;   // qualifying product already first
        var lead = firstNonGift(cart);
        if (!lead) return;

        var addBody = { id: lead.variant_id, quantity: lead.quantity };
        if (lead.selling_plan_allocation && lead.selling_plan_allocation.selling_plan) {
          addBody.selling_plan = lead.selling_plan_allocation.selling_plan.id;
        }
        var props = null;
        if (lead.properties) {
          for (var k in lead.properties) {
            if (lead.properties.hasOwnProperty(k)) { props = props || {}; props[k] = lead.properties[k]; }
          }
        }
        if (props) addBody.properties = props;

        function readd(attempt) {
          return fetch(ADD_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: JSON.stringify({ items: [addBody] })
          }).then(function (r) {
            if (!r.ok && attempt < 2) return readd(attempt + 1);
          });
        }

        // Remove the lead line, then immediately re-add it so it becomes newest (top).
        return fetch(CHANGE_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ id: lead.key, quantity: 0 })
        }).then(function () { return readd(0); })
          .then(function () {
            document.dispatchEvent(new CustomEvent('cart:build', { bubbles: true }));
            document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
          });
      });
    }

    function qualifies(cart) {
      var others = (cart.items || []).filter(function (i) { return i.handle !== GIFT_HANDLE; });
      if (!others.length) return false;
      if (!TRIGGER_HANDLES.length) return true;
      return others.some(function (i) { return TRIGGER_HANDLES.indexOf(i.handle) !== -1; });
    }

    function change(key, qty) {
      return fetch(CHANGE_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ id: key, quantity: qty })
      }).then(function () {
        // `cart:build` makes Motion rebuild the drawer UI (so the removed gift
        // actually disappears); `cart:updated` refreshes ancillary cart UI.
        document.dispatchEvent(new CustomEvent('cart:build', { bubbles: true }));
        document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
      });
    }

    function addGift() {
      return resolveVariant().then(function (id) {
        if (!id) return;
        return fetch(ADD_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ items: [{ id: id, quantity: GIFT_QTY, properties: { _free_gift: 'true' } }] })
        }).then(function () {
          document.dispatchEvent(new CustomEvent('ajaxProduct:added', { bubbles: true }));
        });
      });
    }

    function reconcile(cart) {
      if (busy || !cart) return;
      var line = giftLine(cart);
      var ok = qualifies(cart);

      if (ok && !line) {
        busy = true;
        addGift()
          .then(function () { return reorderGiftLast(); })
          .catch(function () {})
          .then(function () { busy = false; });
      } else if (!ok && line) {
        // Gift is in the cart but nothing qualifies it any more (e.g. the belt
        // was removed) — pull the gift back out too.
        busy = true;
        change(line.key, 0).catch(function () {}).then(function () { busy = false; });
      } else if (line && line.quantity > GIFT_QTY) {
        // Keep the gift pinned at the free quantity.
        busy = true;
        change(line.key, GIFT_QTY).catch(function () {}).then(function () { busy = false; });
      }
    }

    document.addEventListener('cart:updated', function (e) {
      if (e.detail && e.detail.cart) reconcile(e.detail.cart);
      else getCart().then(reconcile);
    });
    document.addEventListener('ajaxProduct:added', function () {
      getCart().then(reconcile);
    });

    getCart().then(reconcile);
  }

  function init() {
    // initFreeGift (theme-native watcher) is intentionally omitted: free gifts
    // and upsells are now driven by Kaching via the add-to-cart bridge
    // (kachingExtraItems). Running both would double-add the gift.
    [initUpsell, initStickyBar, initHero, initUgc, initQa, initSocialProof, initReviewLink, initGiftGuard].forEach(function (fn) {
      try { fn(); } catch (e) { if (window.console) console.warn('[cro]', e); }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
